import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { configuration as configurationEndpoint } from 'config/backendRoutes';
import { FOUNDATION } from 'config/features';
import { del, get, post, put } from 'util/fetch';
import { useOrganizationShortName } from '../../../store/hooks/useOrganizationShortName';
import { useToken } from '../../../store/hooks/useToken';
import { QUERY_KEYS } from './queryKeys';

const v1Url = organizationShortName => `${configurationEndpoint}/v1/${organizationShortName}/configuration/features`;

const v2Url = organizationShortName => `${configurationEndpoint}/v2/${organizationShortName}/features`;

export const featuresV2 = (token, organizationShortName) => get(v2Url(organizationShortName), { token });

export const createFeature = (token, organizationShortName, body) =>
  post(v1Url(organizationShortName), { token, body });
export const fetchAllFeatures = (token, organizationShortName) => get(v1Url(organizationShortName), { token });
export const editFeature = (token, organizationShortName, key_id, body) =>
  put(`${v1Url(organizationShortName)}/${key_id}`, { token, body });
export const deleteFeature = (token, organizationShortName, key_id) =>
  del(`${v1Url(organizationShortName)}/${key_id}`, { token });

export const useGetFeatures = () => {
  const token = useToken();
  const organizationShortName = useOrganizationShortName();
  return useQuery({
    queryKey: [QUERY_KEYS.FEATURES, { token, organizationShortName }],
    queryFn: async ({ queryKey }) => {
      const [, { token, organizationShortName }] = queryKey;
      const features = await featuresV2(token, organizationShortName);
      return features;
    },
    enabled: !!token && !!organizationShortName && !FOUNDATION,
  });
};

export const useGetAllFeatures = () => {
  const token = useToken();
  const organizationShortName = useOrganizationShortName();
  return useQuery({
    queryKey: [QUERY_KEYS.ALL_FEATURES, { token, organizationShortName }],
    queryFn: async ({ queryKey }) => {
      const [, { token, organizationShortName }] = queryKey;
      const features = await fetchAllFeatures(token, organizationShortName);
      return features;
    },
    initialData: [],
    enabled: !!token && !!organizationShortName,
  });
};

export const useCreateFeature = () => {
  const token = useToken();
  const organizationShortName = useOrganizationShortName();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ body }) => {
      await createFeature(token, organizationShortName, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.FEATURES, QUERY_KEYS.ALL_FEATURES]);
    },
  });
};

export const useEditFeature = () => {
  const token = useToken();
  const organizationShortName = useOrganizationShortName();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ key_id, body }) => {
      await editFeature(token, organizationShortName, key_id, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.FEATURES, QUERY_KEYS.ALL_FEATURES]);
    },
  });
};

export const useDeleteFeature = () => {
  const token = useToken();
  const organizationShortName = useOrganizationShortName();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ key_id }) => {
      await deleteFeature(token, organizationShortName, key_id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.FEATURES, QUERY_KEYS.ALL_FEATURES]);
    },
  });
};
